import sampleOfferImage from '~shared-assets/images/sample-offer.png';

import { Image } from 'antd';
import { CSSProperties } from 'react';
import styled from 'styled-components';

import { TextInput } from '~src/components/BaseInput';
import { BulletPoints } from '~src/components/BulletPoints/BulletPoints';
import { EmailInput } from '~src/components/EmailInput';
import { PhoneNumberInput } from '~src/components/PhoneNumberInput';
import { LabelSwitch } from '~src/components/Switch';
import { Text } from '~src/components/Typography';
import { CustomerWithExtraData, useCustomerContext } from '~src/context/CustomerContext';
import { useDisplayContext } from '~src/context/DisplayContext';
import { useLeadOrigin } from '~src/hooks/useLeadOrigin';
import { CustomerDetails } from '~src/model';

import { useSwitchEntries } from './common';
import { CALCULATION_BULLET_POINTS_ENTRIES, CALCULATION_BULLET_POINTS_TITLE } from './constants';
import { useGdprConsent } from './useGdprConsent';

const SendOfferWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '40px',
});

type CustomerInformationProps = {
  customer?: Partial<CustomerWithExtraData>;
  isLoading?: boolean;
  setCustomer?: (value?: Partial<CustomerWithExtraData>) => void;
};

export const CustomerInformation = ({ customer, isLoading, setCustomer }: CustomerInformationProps) => {
  const { isMobile } = useDisplayContext();
  const consentText = useGdprConsent();
  const { origin } = useLeadOrigin();

  const updateState = (input: Partial<CustomerDetails>) => setCustomer?.({ ...customer, ...input });

  const {
    firstName: customerFirstName,
    lastName: customerLastName,
    email: customerEmail,
    phoneNumber: customerPhoneNumber,
  } = customer ?? {};

  const switchEntries = useSwitchEntries({ customer, setCustomer });

  const inputFieldsStyle: CSSProperties = isMobile
    ? { margin: '16px 0', width: '100%' }
    : { display: 'flex', flexDirection: 'column', margin: '8px 0', justifyContent: 'space-between' };

  return (
    <SendOfferWrapper>
      <Text>
        Bodils energiberegning giver et overblik over pris, og hvor mange penge du kan spare ved at skifte til en
        varmepumpe eller ved at få installeret solceller. Del dine oplysninger med os, og vi sørger for at levere din
        personlige beregning.
      </Text>

      <div
        style={{
          display: 'flex',
          ...(isMobile && { flexDirection: 'column', marginBottom: '24px' }),
          gap: '5%',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ ...inputFieldsStyle }}>
          <TextInput
            disabled={isLoading}
            {...{ value: customerFirstName ?? undefined, placeholder: 'Hans', label: 'Fornavn' }}
            onChange={firstName => updateState({ firstName })}
          />

          <TextInput
            disabled={isLoading}
            {...{ value: customerLastName ?? undefined, placeholder: 'Hansen', label: 'Efternavn' }}
            onChange={lastName => updateState({ lastName })}
          />

          <EmailInput
            disabled={isLoading}
            value={customerEmail ?? undefined}
            onChange={email => updateState({ email })}
          />

          <PhoneNumberInput
            disabled={isLoading}
            value={customerPhoneNumber ?? undefined}
            label='Telefonnummer'
            onChange={phoneNumber => updateState({ phoneNumber })}
          />
        </div>

        {!isMobile && <Image src={sampleOfferImage} preview={false} height={'300px'} />}

        <BulletPoints title={CALCULATION_BULLET_POINTS_TITLE} entries={CALCULATION_BULLET_POINTS_ENTRIES} />
      </div>

      {origin !== 'jyskebank' && <Text>{consentText}</Text>}

      <div style={{ margin: '0', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {switchEntries.map(value => (
          <LabelSwitch disabled={isLoading} {...value} key={value.key} />
        ))}
      </div>
    </SendOfferWrapper>
  );
};

export const CustomerInformationUsingContext = () => {
  const { customer, setCustomer } = useCustomerContext();
  return <CustomerInformation {...{ customer, setCustomer }} />;
};

export { CustomerInformationNorlys } from './norlys';
